"use client"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Chip, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React from "react"
import { getNonUmbracoUrl } from "utils"
import { transformProperties } from "utils/simplifyData"
import ButtonWithArrow from "../common/bit/ButtonWithArrow"
import IconWithBackground from "../common/bit/IconWithBackground"
import InfoLink from "../common/bit/InfoLink"
import { colors, mediaQueries, sizes } from "../theme/theme"

interface BulletCardProps {
  item: any
  buttonVariant?: "elevated" | "outlined" | undefined
  cardHeight?: string
  onButtonClick?: (item: BulletCardItem, categoryName?: string) => void
  categoryName?: string
}

export interface BulletCardItem {
  title: any
  properties: {
    title: {
      value: string
    }
    text: {
      value: string
    }
    bullets: {
      richText: string
    }
    button: {
      links: {
        url: string
        name: string
      }[]
    }
  }
}

const StyledBulletCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cardHeight",
})<{ cardHeight?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border-radius: ${sizes.baseDouble};
  background-color: ${colors.white};
  padding: ${sizes.baseQuadric};
  width: 356px;
  max-height: unset;
  .title {
    font-size: 16px;
    text-align: center;
  }
  .text {
    text-align: center;
  }
  ${mediaQueries.tablet} {
    height: 100%;
    width: 277px;
    .title,
    .text {
      width: 275px;
    }
  }
  ${mediaQueries.tabletDown} {
    height: ${(props) => props.cardHeight || "unset"};
  }
  ${mediaQueries.desktopM} {
    width: 374px;
    height: ${(props) => props.cardHeight || "unset"};
    .title,
    .text {
      width: 270px;
    }
  }
  ${mediaQueries.desktopXl} {
    width: 374px;
    max-width: unset;
    .title,
    .text {
      width: 365px;
    }
  }
`
const CardHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid ${colors.lightGray};
  padding-bottom: ${sizes.baseTriple};
  width: 100%;
  height: 146px;
  flex: 0 0 auto;
`
const IconWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: ${sizes.radius.large};
  background-color: ${colors.white};
  margin-bottom: ${sizes.baseSingle};
  height: 48px;
  .title {
    font-size: 16px;
  }
`
const BulletList = styled(Typography)<{ component?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid ${colors.lightGray};
  padding-bottom: ${sizes.baseSingle};
  margin-bottom: ${sizes.baseDouble};
  height: 100%;
  width: 100%;
  ${mediaQueries.tabletDown} {
    min-height: 120px;
  }
  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    list-style-type: none;
    overflow: hidden;
    li {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      color: ${colors.black};
      &::before {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        height: 100%;
        content: url("/icons/check.svg");
        margin-right: ${sizes.baseSingle};
      }
    }
  }
`
const StyledButtonWithArrow = styled(ButtonWithArrow)`
  padding: 10px 18px;
  margin-bottom: ${sizes.baseDouble};
  svg {
    width: unset;
  }
  ${mediaQueries.desktopM} {
    margin-top: auto;
    padding: 10px 18px;
  }
`
const StyledChip = styled(Chip)`
  border: 1px solid ${colors.red};
  background-color: ${colors.white};
  color: ${colors.black};
  z-index: 10;
`
const StyledChipWrapper = styled(Box)`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: -10px;
  width: 100%;
`

const BulletCard: React.FC<BulletCardProps> = ({
  item,
  buttonVariant,
  onButtonClick,
  categoryName,
  cardHeight,
}) => {
  const currItem = item?.properties
    ? transformProperties(item?.properties)
    : item
  const { icon, title, text, bullets, button, link, tag, primaryColor } =
    currItem
  const content = bullets?.richText || bullets || ""
  const t = useTranslations("SEO")
  return (
    <StyledBulletCard
      key={`bullet-card-item-${title?.value}`}
      cardHeight={cardHeight}
    >
      {((tag && typeof tag === "string") || tag?.value) && (
        <StyledChipWrapper>
          <StyledChip label={tag?.value || tag} size="small" />
        </StyledChipWrapper>
      )}
      <CardHeader>
        <IconWrapper>
          {icon && (icon?.mediaItems?.[0] || icon?.src) && (
            <IconWithBackground
              size={48}
              icon={<Image src={icon} alt={`${t("iconAlt")}${title}`} fill />}
              backgroundColor={
                (colors.resolveColorName("#" + primaryColor) as any) ||
                colors.turquoise
              }
            />
          )}
        </IconWrapper>
        {title && (
          <Typography variant="h5" component="h2" className="title">
            {title?.value || title}
          </Typography>
        )}
        {text && (
          <Typography variant="body1" component="h3" className="text">
            {text?.value || text}
          </Typography>
        )}
      </CardHeader>
      {bullets && (
        <BulletList
          variant="body1"
          component="div"
          dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(content) }}
        />
      )}
      {button && (button.links?.[0] || button?.text || button?.href) && (
        <StyledButtonWithArrow
          variant={buttonVariant}
          arrowName={
            buttonVariant === "outlined"
              ? "arrow-black-down"
              : "arrow_orange_white_line"
          }
          href={getNonUmbracoUrl(button?.links?.[0]?.url || button?.href)}
          onClick={() => onButtonClick?.(item, categoryName)}
        >
          {button?.links?.[0]?.name || button?.text}
        </StyledButtonWithArrow>
      )}
      {link && (
        <InfoLink
          href={getNonUmbracoUrl(link?.links?.[0]?.url || link?.href)}
          onClick={() => onButtonClick?.(item, categoryName)}
        >
          {link?.links?.[0]?.name || link?.text}
        </InfoLink>
      )}
    </StyledBulletCard>
  )
}

export default BulletCard
